import { supabase } from '../../lib/supabase'
import { isSupabaseConfigured } from '../../config/env'
import { UserProfile, Membership } from '../../types'

export interface AuthSessionUser {
  id: string
  email: string
  profile?: UserProfile
  memberships: Membership[]
  is_system_admin?: boolean
}

export const authService = {
  async signIn(email: string, password: string): Promise<{ user: AuthSessionUser | null; error: Error | null }> {
    if (!isSupabaseConfigured() || !supabase) {
      return {
        user: null,
        error: new Error('El backend de Supabase no está configurado. Configura las variables de entorno para autenticarte.'),
      }
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (error || !data.user) {
      return { user: null, error: error || new Error('No se pudo iniciar sesión') }
    }

    const profile = await this.getProfile(data.user.id)
    const memberships = await this.getMemberships(data.user.id)

    return {
      user: {
        id: data.user.id,
        email: data.user.email || email,
        profile,
        memberships,
        is_system_admin: Boolean(profile?.is_system_admin),
      },
      error: null,
    }
  },

  async signOut(): Promise<{ error: Error | null }> {
    if (!supabase) return { error: null }
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  async requestPasswordReset(email: string): Promise<{ error: Error | null }> {
    if (!isSupabaseConfigured() || !supabase) {
      return {
        error: new Error('Servicio de correo y backend no configurados en este entorno.'),
      }
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    return { error }
  },

  async updatePassword(password: string): Promise<{ error: Error | null }> {
    if (!isSupabaseConfigured() || !supabase) {
      return {
        error: new Error('El cliente no dispone de conexión activa con Supabase.'),
      }
    }

    const { error } = await supabase.auth.updateUser({ password })
    return { error }
  },

  async getCurrentSessionUser(): Promise<AuthSessionUser | null> {
    if (!isSupabaseConfigured() || !supabase) {
      return null
    }

    const { data: { session } } = await supabase.auth.getSession()
    if (!session || !session.user) {
      return null
    }

    const profile = await this.getProfile(session.user.id)
    const memberships = await this.getMemberships(session.user.id)

    return {
      id: session.user.id,
      email: session.user.email || '',
      profile,
      memberships,
      is_system_admin: Boolean(profile?.is_system_admin),
    }
  },

  async getProfile(userId: string): Promise<UserProfile | undefined> {
    if (!supabase) return undefined
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (error) {
      throw new Error(`Error en base de datos al consultar perfil: ${error.message} (código ${error.code})`)
    }

    return data || undefined
  },

  async getMemberships(userId: string): Promise<Membership[]> {
    if (!supabase) return []
    const { data, error } = await supabase
      .from('memberships')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)

    if (error) {
      throw new Error(`Error en base de datos al consultar membresías y roles: ${error.message} (código ${error.code})`)
    }

    return data || []
  },

  async validateInvitationToken(token: string): Promise<{
    status: 'valid' | 'expired' | 'used' | 'revoked' | 'not_found'
    invitation?: { email: string; organization_id: string; team_id?: string; roles: string[] }
  }> {
    if (!supabase) return { status: 'not_found' }
    const { data, error } = await supabase.functions.invoke('accept-invitation', { body: { action: 'inspect', token } })
    if (error || !data) return { status: 'not_found' }
    return data
  },

  async acceptInvitationWithPassword(token: string, password: string): Promise<{ success: boolean; error: Error | null }> {
    if (!supabase) return { success: false, error: new Error('Backend no configurado') }
    const { data: { user } } = await supabase.auth.getUser()
    const { data, error } = await supabase.functions.invoke('accept-invitation', { body: { token, ...(user ? {} : { password }) } })
    if (error || !data?.success) {
      const payload = error?.context ? await error.context.json().catch(() => null) : data
      return { success: false, error: new Error(payload?.message || ({
        PASSWORD_MIN_12: 'La contraseña debe tener al menos 12 caracteres.',
        EXISTING_ACCOUNT_SIGN_IN: 'Inicia sesión con tu cuenta existente y vuelve a abrir la invitación.',
        ONBOARDING_IN_PROGRESS: 'La incorporación está en curso. Reintenta en unos instantes.',
        LINK_RETRY: 'La cuenta está creada. Reintenta para completar la vinculación.',
      } as Record<string,string>)[payload?.error] || 'No se pudo aceptar la invitación. Comprueba la sesión y reintenta.') }
    }
    if (!user && data.email) {
      const { error: loginError } = await supabase.auth.signInWithPassword({ email: data.email, password })
      if (loginError) return { success: false, error: new Error('Cuenta vinculada. Inicia sesión con tu contraseña original.') }
    }
    return { success: true, error: null }
  },

  /**
   * Acepta una invitación directamente con la sesión activa autenticada
   */
  async acceptInvitationDirect(token: string): Promise<{ success: boolean; error: Error | null }> {
    if (!supabase) return { success: false, error: new Error('Supabase no configurado.') }
    return this.acceptInvitationWithPassword(token, '')
  },
}

